import { useEffect, useState } from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { TextArea } from '@progress/kendo-react-inputs';
import { Error, Label } from '@progress/kendo-react-labels';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { PrivatePage } from '../../components/private-page';

import Avatar from '../../components/avatar';
import {
  GetSupportTicketByIdAsync,
  GetTicketAttachByIdAsync,
  PatchSupportTicketAsync
} from '../../services/support-tickets';
import { SupportTicket, TicketAttachments } from '../../types/support-ticket';
import { formatDateToDDMMYYYY } from '../../utils/date';
import { Browsers, Devices } from '../../constants/user';
import { useToggle } from 'usehooks-ts';
import MediaPreviewDialog from '../../components/dialogs/media-preview-dialog';
import { useToast } from '../../contexts/toast-context';

const SupportTicketDetail = () => {
  const { id } = useParams();
  const statusList = ['New', 'InProgress', 'Closed'];
  const [data, setData] = useState<SupportTicket>({});
  const [isEmptyNotes, setIsEmptyNotes] = useState(false);
  const [previewItem, setPreviewItem] = useState<{
    url: string;
    type: string;
  }>({
    url: '',
    type: ''
  });
  const { onToastOpen } = useToast();

  type BrowserKeys = keyof typeof Browsers;
  type DeviceKeys = keyof typeof Devices;

  // fetch environment data function
  const fetchData = async (envId: number) => {
    await GetSupportTicketByIdAsync(envId)
      .then((response) => {
        setData(response);
      })
      .catch((err) => console.error(err));
    await GetTicketAttachByIdAsync(envId)
      .then((response) => {
        setData((prev) => ({
          ...prev,
          supportTicketAttachments: response as TicketAttachments[]
        }));
      })
      .catch((err) => console.error(err));
  };

  // update notes values on change event
  const onChangeNotesEvent = (event: string) => {
    setData({
      ...data,
      notes: event ?? ''
    });
  };

  // update status values on change event
  const onChangeStatusEvent = (event: string) => {
    setData({
      ...data,
      status: event ?? 'New'
    });
  };

  const updateSupportTicket = async () => {
    try {
      if (data) {
        if (!data.notes) {
          setIsEmptyNotes(true);
          return;
        } else {
          setIsEmptyNotes(false);
        }

        const request = { ...data };
        await PatchSupportTicketAsync(data.id!, request);
        await fetchData(parseInt(id!));
        onToastOpen({
          title: 'Support Tickets',
          body: <div>Support ticket saved.</div>,
          style: 'success'
        });
      }
    } catch (err) {
      onToastOpen({
        title: 'Support Tickets',
        body: <div>Failed to save support ticket.</div>,
        style: 'error'
      });
    }
  };

  const getBrowserName = (value: BrowserKeys) => {
    if (Browsers.hasOwnProperty(value)) {
      return Browsers[value];
    } else {
      return '';
    }
  };

  const getDeviceName = (value: DeviceKeys) => {
    if (Devices.hasOwnProperty(value)) {
      return Devices[value];
    } else {
      return '';
    }
  };

  const getMediaType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (extension) {
      // prettier-ignore
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'];
      // prettier-ignore
      const videoTypes = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'mpeg'];

      if (imageTypes.includes(extension)) return 'image';
      if (videoTypes.includes(extension)) return 'video';
    }

    return 'other';
  };

  useEffect(() => {
    if (id) {
      fetchData(parseInt(id));
    }
  }, [id]);

  return (
    <PrivatePage
      breadcrumb={[
        { label: 'Dashboard', href: 'dashboard' },
        { label: 'Administration', href: 'administration' },
        { label: 'Support Tickets', href: 'administration/support_tickets' },
        {
          label: 'Support Ticket details',
          href: `administration/support_tickets/${id}`
        }
      ]}
      pageTitle={'Administration'}>
      <Container>
        <Row className={'border-bottom pb-4'}>
          <Col lg={4} md={6} sm={6} className={'d-flex align-items-center'}>
            <h1 className={'m-0'}>{data.title}</h1>
          </Col>
          <Col lg={4} md={6} sm={6}>
            <div className={'d-flex gap-2 justify-content-end'}>
              <Button
                variant={'primary'}
                className={'text-light'}
                onClick={updateSupportTicket}>
                Update
              </Button>
            </div>
          </Col>
        </Row>
        <Row className={'mt-3'}>
          <Col lg={4} md={6}>
            <div
              className={
                'author-card border border-1 bg-light p-2 rounded d-inline-block w-100 mb-2'
              }>
              <Avatar
                name={data.requestedBy}
                img={
                  'https://images.unsplash.com/photo-1657003100487-3ab018a43b67?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHx0b3BpYy1mZWVkfDh8NnNNVmpUTFNrZVF8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=60'
                }
              />
              <Row className={'mb-2'}>
                <Col>
                  <div>Created on:</div>
                  <div>{formatDateToDDMMYYYY(data.createdAt!, true)}</div>
                </Col>
                <Col>
                  <div>Last updated on:</div>
                  <div>{formatDateToDDMMYYYY(data.modifiedDateUtc!, true)}</div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className={'text-muted text-sm'}>Device Type:</div>
                  <div>
                    {data.device
                      ? getDeviceName(data.device as DeviceKeys)
                      : '-'}
                  </div>
                </Col>
                <Col>
                  <div>Browser:</div>
                  <div>
                    {data.browser
                      ? getBrowserName(data.browser as BrowserKeys)
                      : '-'}
                  </div>
                </Col>
              </Row>
            </div>
            <div
              className={
                'author-card border border-1 bg-light p-2 rounded d-inline-block w-100 mb-2'
              }>
              <Row>
                <Col>
                  <div>Description:</div>
                  <div>{data.description || '-'}</div>
                </Col>
              </Row>
            </div>
            <div
              className={
                'author-card border border-1 bg-light p-2 rounded d-inline-block w-100'
              }>
              <Row>
                <Col>
                  <div>Attachments:</div>
                  <div className={'d-flex gap-2 flex-wrap'}>
                    {data?.supportTicketAttachments &&
                    data?.supportTicketAttachments?.length > 0
                      ? data.supportTicketAttachments?.map(
                          (item: TicketAttachments, index: number) => (
                            <div className={'d-flex'} key={index}>
                              <div
                                key={index}
                                className={
                                  'w-6 h-6 bg-light position-relative rounded d-flex overflow-hidden flex-column justify-content-center align-items-center cursor-pointer'
                                }
                                onClick={() =>
                                  setPreviewItem({
                                    url: item.filePath,
                                    type: getMediaType(item.filePath)
                                  })
                                }>
                                {item &&
                                  getMediaType(item.filePath) === 'video' && (
                                    <div
                                      className={
                                        'mask position-absolute top-0 end-0 h-full w-full d-flex align-items-center justify-content-center'
                                      }>
                                      <button className={'btn text-light'}>
                                        <span
                                          className={
                                            'material-symbols-outlined'
                                          }>
                                          play_arrow
                                        </span>
                                      </button>
                                    </div>
                                  )}
                                {item &&
                                  getMediaType(item.filePath) === 'image' && (
                                    <img
                                      src={item.filePath}
                                      className={'rounded'}
                                      alt={''}
                                    />
                                  )}
                              </div>
                            </div>
                          )
                        )
                      : '-'}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col lg={4} md={6} className={'d-flex flex-column gap-2'}>
            <DropDownList
              style={{
                backgroundColor: 'white',
                width: '100%'
              }}
              defaultValue={'New'}
              value={data.status}
              data={statusList}
              popupSettings={{
                animate: false
              }}
              onChange={(e) => onChangeStatusEvent(e.value)}
            />
            <div>
              <Label>Notes*</Label>
              <TextArea
                id={'notes'}
                maxLength={256}
                rows={4}
                resizable="none"
                value={data?.notes}
                onChange={(e) => {
                  onChangeNotesEvent(e.value);
                }}
              />
              {isEmptyNotes && <Error>This field is required.</Error>}
            </div>
          </Col>
        </Row>
      </Container>

      {previewItem.url && previewItem.type && (
        <MediaPreviewDialog
          title="Media Preview"
          mediaType={previewItem.type}
          mediaUrl={previewItem.url}
          onClose={() => {
            setPreviewItem({
              type: '',
              url: ''
            });
          }}
        />
      )}
    </PrivatePage>
  );
};

export default SupportTicketDetail;
